import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { businessAPI } from '../services/api';
import {
  MapPin,
  Phone,
  Mail,
  Globe,
  Building2,
  Tag,
  Users,
  Calendar,
  Clock,
  Star,
  Briefcase,
  CheckCircle,
  Loader2,
  ArrowLeft,
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  ExternalLink,
  User,
  Edit,
  Trash2,
  AlertTriangle,
  Package,
  Award,
  Shield
} from 'lucide-react';

interface UserType {
  _id: string;
  name: string;
  email: string;
  phone?: string;
  role?: string;
}

interface Rating {
  _id?: string;
  user: UserType | string;
  rating: number;
  createdAt: string;
}

interface Business {
  _id: string;
  name: string;
  tradeName?: string;
  legalName?: string;
  legalForm?: string;
  registrationNumber?: string;
  vatNumber?: string;
  intraCommunityVatNumber?: string;
  shareCapital?: number;
  dateOfIncorporation?: string;
  category?: string;
  industry?: string;
  description?: string;
  website?: string;
  email?: string;
  phone?: string;
  landlinePhone?: string;
  mobilePhone?: string;
  address?: {
    street?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
  };
  tags?: string[];
  products?: string[];
  services?: string[];
  employees?: number;
  yearFounded?: number;
  revenue?: number;
  rating?: number;
  ratings?: Rating[];
  verified?: boolean;
  logo?: string;
  images?: string[];
  certifications?: Array<{
    name: string;
    issuer?: string;
    date?: string;
    expiryDate?: string;
    certificateNumber?: string;
    description?: string;
  }>;
  leadership?: Array<{
    name: string;
    position: string;
  }>;
  socialMedia?: {
    facebook?: string;
    twitter?: string;
    instagram?: string;
    linkedin?: string;
  };
  openingHours?: {
    monday?: { open?: string; close?: string; closed?: boolean };
    tuesday?: { open?: string; close?: string; closed?: boolean };
    wednesday?: { open?: string; close?: string; closed?: boolean };
    thursday?: { open?: string; close?: string; closed?: boolean };
    friday?: { open?: string; close?: string; closed?: boolean };
    saturday?: { open?: string; close?: string; closed?: boolean };
    sunday?: { open?: string; close?: string; closed?: boolean };
  };
  internationalPresence?: boolean;
  createdAt?: string;
  userId?: UserType | string;
  location?: {
    type?: string;
    coordinates?: number[];
  };
  slug?: string;
  viewCount?: number;
  visibility?: 'public' | 'private';
  termsAccepted?: boolean;
}

const RESERVED_ROUTES = [
  'login', 'register', 'dashboard', 'admin', 'search', 'analytics',
  'businesses', 'forum', 'news', 'events', 'actualities', 'profile',
  'create-business', 'api', 'uploads'
];

const OPENING_HOURS_LABELS: { key: keyof NonNullable<Business['openingHours']>; label: string }[] = [
  { key: 'monday', label: 'Lundi' },
  { key: 'tuesday', label: 'Mardi' },
  { key: 'wednesday', label: 'Mercredi' },
  { key: 'thursday', label: 'Jeudi' },
  { key: 'friday', label: 'Vendredi' },
  { key: 'saturday', label: 'Samedi' },
  { key: 'sunday', label: 'Dimanche' }
];

const BusinessDetail: React.FC = () => {
  const { id, slug } = useParams<{ id?: string; slug?: string }>();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const [business, setBusiness] = useState<Business | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [userRating, setUserRating] = useState<number | null>(null);
  const [ratingBusiness, setRatingBusiness] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('about');

  useEffect(() => {
    if (slug && RESERVED_ROUTES.includes(slug.toLowerCase())) {
      navigate('/');
      return;
    }
    if (id || slug) {
      loadBusiness();
    }
  }, [id, slug, user, navigate]);

  const loadBusiness = async () => {
    if (!id && !slug) return;

    setLoading(true);
    setError(null);
    try {
      let response;
      if (slug) {
        const decodedSlug = decodeURIComponent(slug);
        response = await businessAPI.getBusinessBySlug(decodedSlug);
      } else if (id) {
        response = await businessAPI.getBusiness(id);
      } else {
        setError('No business identifier provided');
        setLoading(false);
        return;
      }

      if (response.data.success) {
        setBusiness(response.data.data);
        if (user && response.data.data.ratings) {
          const userRatingObj = response.data.data.ratings.find((r: Rating) => {
            const ratingUserId = typeof r.user === 'object' ? r.user._id : r.user;
            return ratingUserId === user._id;
          });
          if (userRatingObj) {
            setUserRating(userRatingObj.rating);
          }
        }
      } else {
        setError(response.data.message || 'Failed to load business');
      }
    } catch (err: any) {
      console.error('Error loading business:', err);
      if (err.response?.status === 404) {
        setError('Business not found');
      } else {
        setError(err.response?.data?.message || 'Failed to load business');
      }
    } finally {
      setLoading(false);
    }
  };

  const getBusinessAddress = (business: Business): string => {
    if (!business.address) return 'Address not available';
    const parts = [
      business.address.street,
      business.address.city,
      business.address.zipCode,
    ].filter(Boolean);
    return parts.join(', ') || 'Address not available';
  };

  const getMapUrl = (business: Business): string => {
    if (business.location?.coordinates &&
        business.location.coordinates.length === 2 &&
        business.location.coordinates[0] !== 0 &&
        business.location.coordinates[1] !== 0) {
      const [lng, lat] = business.location.coordinates;
      return `https://maps.google.com/maps?q=${lat},${lng}&hl=en&z=14&output=embed`;
    }
    const address = getBusinessAddress(business);
    if (address && address !== 'Address not available') {
      return `https://maps.google.com/maps?q=${encodeURIComponent(address)}&hl=en&z=14&output=embed`;
    }
    return '';
  };

  const getGoogleMapsSearchUrl = (business: Business): string => {
    if (business.location?.coordinates &&
        business.location.coordinates.length === 2 &&
        business.location.coordinates[0] !== 0 &&
        business.location.coordinates[1] !== 0) {
      const [lng, lat] = business.location.coordinates;
      return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
    }
    const address = getBusinessAddress(business);
    if (address && address !== 'Address not available') {
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
    }
    return '';
  };

  const isOwner = () => {
    if (!user || !business) return false;
    if (typeof business.userId === 'object') {
      return business.userId._id === user._id;
    }
    return business.userId === user._id;
  };

  const businessId = business?._id ?? id;

  const handleDelete = async () => {
    if (!businessId || !business) return;
    setDeleting(true);
    try {
      await businessAPI.deleteBusiness(businessId);
      navigate('/businesses');
    } catch (err: any) {
      console.error('Error deleting business:', err);
      setError(err.response?.data?.message || 'Failed to delete business');
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleRate = async (rating: number) => {
    if (!businessId || !user) {
      setError('You must be logged in to rate businesses');
      return;
    }
    setRatingBusiness(true);
    setError(null);
    try {
      const response = await businessAPI.rateBusiness(businessId, rating);
      if (response.data.success) {
        setUserRating(rating);
        setBusiness(response.data.data);
      } else {
        setError(response.data.message || 'Failed to rate business');
      }
    } catch (err: any) {
      console.error('Error rating business:', err);
      setError(err.response?.data?.message || 'Failed to rate business');
    } finally {
      setRatingBusiness(false);
    }
  };

  const handleDeleteRating = async () => {
    if (!businessId || !user) return;
    setRatingBusiness(true);
    setError(null);
    try {
      const response = await businessAPI.deleteRating(businessId);
      if (response.data.success) {
        setUserRating(null);
        setBusiness(response.data.data);
      } else {
        setError(response.data.message || 'Failed to remove rating');
      }
    } catch (err: any) {
      console.error('Error removing rating:', err);
      setError(err.response?.data?.message || 'Failed to remove rating');
    } finally {
      setRatingBusiness(false);
    }
  };

  const formatTimeAgo = (date: string) => {
    const now = new Date();
    const ratingDate = new Date(date);
    const diffInSeconds = Math.floor((now.getTime() - ratingDate.getTime()) / 1000);
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return ratingDate.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-green-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading business details...</p>
        </div>
      </div>
    );
  }

  if (error || !business) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <Briefcase className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Business Not Found</h2>
          <p className="text-gray-600 mb-6">{error || 'The business you are looking for does not exist.'}</p>
          <Link
            to="/businesses"
            className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Business Directory
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between mb-6">
            <Link to="/businesses" className="inline-flex items-center text-gray-600 hover:text-gray-900">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to directory
            </Link>
            {isOwner() && (
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => navigate('/dashboard/create-business')}
                  className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Modifier
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </button>
              </div>
            )}
          </div>

          <div className="flex items-start justify-between mb-6">
            <div className="flex items-start space-x-4 flex-1">
              {business.logo ? (
                <img
                  src={business.logo}
                  alt={`${business.name} logo`}
                  className="h-24 w-24 object-contain rounded border-2 border-gray-200 bg-white shadow-sm flex-shrink-0"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              ) : (
                <div className="h-24 w-24 bg-gradient-to-br from-green-500 to-blue-600 rounded flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-2xl font-bold">{business.name.charAt(0).toUpperCase()}</span>
                </div>
              )}
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <h1 className="text-3xl font-bold text-gray-900">{business.name}</h1>
                  {business.verified && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Entreprise certifiée
                    </span>
                  )}
                </div>
                {business.address && (
                  <div className="flex items-center text-gray-600 mb-4">
                    <MapPin className="h-4 w-4 mr-1" />
                    <span>
                      {business.address.zipCode && `${business.address.zipCode} `}
                      {business.address.city && business.address.city.toUpperCase()}
                      {business.address.country && ` - ${business.address.country}`}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="ml-6">
              <a
                href={`tel:${business.phone || business.landlinePhone || business.mobilePhone || ''}`}
                className="inline-flex items-center px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-lg shadow-sm transition-colors"
              >
                <Phone className="h-5 w-5 mr-2" />
                Contacter l&apos;entreprise
              </a>
            </div>
          </div>

          <div className="border-b border-gray-200">
            <nav className="flex space-x-8">
              {[
                { id: 'about', label: 'Qui sommes nous' },
                { id: 'ratings', label: 'Notations' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-teal-600 text-teal-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </div>
      </div>

      <div className="bg-gray-100 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              {activeTab === 'about' && (
                <>
                  {business.description && (
                    <div className="bg-white rounded-lg shadow-sm p-6">
                      <h2 className="text-lg font-bold text-gray-900 mb-4 uppercase tracking-wide">
                        PRÉSENTATION - {business.name.toUpperCase()}
                      </h2>
                      <p className="text-gray-700 leading-relaxed">{business.description}</p>
                    </div>
                  )}

                  {((business.products && business.products.length > 0) ||
                    (business.services && business.services.length > 0)) && (
                    <div className="bg-white rounded-lg shadow-sm p-6">
                      <h2 className="text-lg font-bold text-gray-900 mb-4 uppercase tracking-wide flex items-center">
                        <Package className="h-5 w-5 mr-2 text-teal-600" />
                        PRODUITS &amp; SERVICES
                      </h2>
                      <div className="grid md:grid-cols-2 gap-6">
                        {business.products && business.products.length > 0 && (
                          <div>
                            <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center">
                              <Tag className="h-4 w-4 mr-1 text-gray-500" />
                              Produits
                            </h3>
                            <ul className="space-y-1 text-sm text-gray-700 list-disc list-inside">
                              {business.products.map((product, idx) => (
                                <li key={`${product}-${idx}`}>{product}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {business.services && business.services.length > 0 && (
                          <div>
                            <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center">
                              <Briefcase className="h-4 w-4 mr-1 text-gray-500" />
                              Services
                            </h3>
                            <ul className="space-y-1 text-sm text-gray-700 list-disc list-inside">
                              {business.services.map((service, idx) => (
                                <li key={`${service}-${idx}`}>{service}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Opening Hours - Horaires d'ouverture */}
                  {business.openingHours &&
                    Object.keys(business.openingHours).length > 0 && (
                      <div className="bg-white rounded-lg shadow-sm p-6">
                        <h2 className="text-lg font-bold text-gray-900 mb-4 uppercase tracking-wide flex items-center">
                          <Clock className="h-5 w-5 mr-2 text-teal-600" />
                          HORAIRES D&apos;OUVERTURE
                        </h2>
                        <dl className="divide-y divide-gray-100">
                          {OPENING_HOURS_LABELS.map(({ key, label }) => {
                            const day = business.openingHours ? business.openingHours[key] : undefined;
                            if (!day) return null;
                            const isClosed = day.closed || (!day.open && !day.close);
                            return (
                              <div key={key} className="flex items-center justify-between py-2 text-sm">
                                <dt className="text-gray-600">{label}</dt>
                                <dd className="font-medium text-gray-900">
                                  {isClosed ? 'Fermé' : `${day.open || '–'} - ${day.close || '–'}`}
                                </dd>
                              </div>
                            );
                          })}
                        </dl>
                      </div>
                    )}

                  {business.leadership && business.leadership.length > 0 && (
                    <div className="bg-white rounded-lg shadow-sm p-6">
                      <h2 className="text-lg font-bold text-gray-900 mb-4 uppercase tracking-wide flex items-center">
                        <Users className="h-5 w-5 mr-2 text-teal-600" />
                        COMPANY LEADERSHIP
                      </h2>
                      <div className="space-y-3">
                        {business.leadership.map((leader, idx) => (
                          <div
                            key={`${leader.name}-${leader.position}-${idx}`}
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                          >
                            <div className="flex items-center">
                              <User className="h-4 w-4 text-gray-500 mr-2" />
                              <span className="font-semibold text-gray-900">{leader.name}</span>
                            </div>
                            <span className="text-sm font-medium text-teal-700">{leader.position}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {business.certifications && business.certifications.length > 0 && (
                    <div className="bg-white rounded-lg shadow-sm p-6">
                      <h2 className="text-lg font-bold text-gray-900 mb-4 uppercase tracking-wide flex items-center">
                        <Award className="h-5 w-5 mr-2 text-teal-600" />
                        CERTIFICATIONS
                      </h2>
                      <div className="space-y-3">
                        {business.certifications.map((cert, idx) => (
                          <div
                            key={`${cert.name}-${cert.certificateNumber || idx}`}
                            className="p-3 bg-gray-50 rounded-lg border border-gray-200"
                          >
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center">
                                <span className="font-semibold text-gray-900">{cert.name}</span>
                                {cert.issuer && (
                                  <span className="ml-2 text-xs text-gray-500">({cert.issuer})</span>
                                )}
                              </div>
                              {cert.date && (
                                <span className="text-xs text-gray-500">
                                  Délivrée le {new Date(cert.date).toLocaleDateString('fr-FR')}
                                </span>
                              )}
                            </div>
                            {cert.expiryDate && (
                              <p className="text-xs text-gray-500">
                                Valide jusqu&apos;au {new Date(cert.expiryDate).toLocaleDateString('fr-FR')}
                              </p>
                            )}
                            {cert.certificateNumber && (
                              <p className="text-xs text-gray-500">N° de certificat : {cert.certificateNumber}</p>
                            )}
                            {cert.description && (
                              <p className="mt-1 text-sm text-gray-700">{cert.description}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {(business.visibility || typeof business.termsAccepted === 'boolean') && (
                    <div className="bg-white rounded-lg shadow-sm p-6">
                      <h2 className="text-lg font-bold text-gray-900 mb-4 uppercase tracking-wide flex items-center">
                        <Shield className="h-5 w-5 mr-2 text-teal-600" />
                        CONDITIONS &amp; VISIBILITÉ
                      </h2>
                      <dl className="space-y-3 text-sm">
                        {business.visibility && (
                          <div className="flex justify-between">
                            <dt className="text-gray-600">Visibilité du profil</dt>
                            <dd className={`font-medium ${business.visibility === 'public' ? 'text-green-700' : 'text-yellow-700'}`}>
                              {business.visibility === 'public' ? 'Publique' : 'Privée'}
                            </dd>
                          </div>
                        )}
                        {typeof business.termsAccepted === 'boolean' && (
                          <div className="flex justify-between">
                            <dt className="text-gray-600">Conditions d&apos;utilisation acceptées</dt>
                            <dd className={`font-medium ${business.termsAccepted ? 'text-green-700' : 'text-red-700'}`}>
                              {business.termsAccepted ? 'Oui' : 'Non'}
                            </dd>
                          </div>
                        )}
                      </dl>
                    </div>
                  )}
                </>
              )}

              {activeTab === 'ratings' && (
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h2 className="text-lg font-bold text-gray-900 mb-4 uppercase tracking-wide">
                    NOTATIONS - {business.name.toUpperCase()}
                  </h2>
                  {user && (
                    <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm font-medium text-gray-700 mb-2">Votre notation</p>
                      <div className="flex items-center gap-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            onClick={() => handleRate(star)}
                            disabled={ratingBusiness}
                            className={`p-1 rounded transition-colors ${
                              (userRating ?? 0) >= star ? 'text-yellow-400' : 'text-gray-300 hover:text-yellow-300'
                            }`}
                          >
                            <Star className="h-6 w-6 fill-current" />
                          </button>
                        ))}
                        {userRating !== null && (
                          <button
                            onClick={handleDeleteRating}
                            disabled={ratingBusiness}
                            className="ml-2 text-sm text-red-600 hover:text-red-700"
                          >
                            Supprimer
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                  {business.ratings && business.ratings.length > 0 ? (
                    <div className="space-y-4">
                      {business.ratings.map((rating, idx) => {
                        const ratingUser = typeof rating.user === 'object' ? rating.user : null;
                        return (
                          <div key={idx} className="border-b border-gray-200 pb-4 last:border-0">
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-semibold text-gray-900">
                                {ratingUser?.name || 'Utilisateur anonyme'}
                              </span>
                              <div className="flex items-center">
                                {[1, 2, 3, 4, 5].map((s) => (
                                  <Star
                                    key={s}
                                    className={`h-4 w-4 ${s <= rating.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
                                  />
                                ))}
                              </div>
                            </div>
                            <p className="text-xs text-gray-500">{formatTimeAgo(rating.createdAt)}</p>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-gray-500">No ratings yet.</p>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-6">
              {business.images && business.images.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h2 className="text-lg font-bold text-gray-900 mb-4 uppercase tracking-wide border-l-4 border-yellow-400 pl-3">
                    COMPANY PHOTOS
                  </h2>
                  <div className="grid grid-cols-2 gap-3">
                    {business.images.slice(0, 4).map((img, idx) => (
                      <div key={idx} className="relative w-full h-20 rounded-lg overflow-hidden border border-gray-200">
                        <img src={img} alt={`${business.name} ${idx + 1}`} className="w-full h-full object-cover" />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(business.website || business.socialMedia) && (
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h2 className="text-lg font-bold text-gray-900 mb-4 uppercase tracking-wide border-l-4 border-teal-500 pl-3">
                    SUIVEZ-NOUS
                  </h2>
                  <div className="space-y-3">
                    {business.website && (
                      <a
                        href={business.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center text-teal-600 hover:text-teal-700"
                      >
                        <Globe className="h-4 w-4 mr-2" />
                        {business.website}
                      </a>
                    )}
                    {business.socialMedia && (
                      <div className="flex flex-wrap gap-2">
                        {business.socialMedia.facebook && (
                          <a
                            href={`https://facebook.com/${business.socialMedia.facebook}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center px-3 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                          >
                            <Facebook className="h-4 w-4 mr-1" />
                            Facebook
                          </a>
                        )}
                        {business.socialMedia.linkedin && (
                          <a
                            href={`https://linkedin.com/company/${business.socialMedia.linkedin}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center px-3 py-2 bg-blue-700 text-white rounded text-sm hover:bg-blue-800"
                          >
                            <Linkedin className="h-4 w-4 mr-1" />
                            LinkedIn
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-lg font-bold text-gray-900 mb-4 uppercase tracking-wide border-l-4 border-green-500 pl-3">
                  INFORMATIONS DE CONTACT
                </h2>
                <div className="space-y-3 text-sm">
                  {business.address && (
                    <div className="flex items-start">
                      <MapPin className="h-4 w-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{getBusinessAddress(business)}</span>
                    </div>
                  )}
                  {business.phone && (
                    <div className="flex items-center">
                      <Phone className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                      <a href={`tel:${business.phone}`} className="text-teal-600 hover:text-teal-700">
                        {business.phone}
                      </a>
                    </div>
                  )}
                  {business.email && (
                    <div className="flex items-center">
                      <Mail className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                      <a href={`mailto:${business.email}`} className="text-teal-600 hover:text-teal-700">
                        {business.email}
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {(business.legalName || business.legalForm || business.registrationNumber ||
                business.vatNumber || business.intraCommunityVatNumber || business.shareCapital) && (
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h2 className="text-lg font-bold text-gray-900 mb-4 uppercase tracking-wide border-l-4 border-green-500 pl-3">
                    INFORMATIONS LÉGALES
                  </h2>
                  <dl className="space-y-3 text-sm">
                    {business.legalName && (
                      <div className="flex justify-between">
                        <dt className="text-gray-600">Dénomination légale</dt>
                        <dd className="font-medium text-gray-900 text-right">{business.legalName}</dd>
                      </div>
                    )}
                    {business.legalForm && (
                      <div className="flex justify-between">
                        <dt className="text-gray-600">Forme juridique</dt>
                        <dd className="font-medium text-gray-900 text-right">{business.legalForm}</dd>
                      </div>
                    )}
                    {business.registrationNumber && (
                      <div className="flex justify-between">
                        <dt className="text-gray-600">Numéro d&apos;enregistrement</dt>
                        <dd className="font-medium text-gray-900 text-right">{business.registrationNumber}</dd>
                      </div>
                    )}
                    {business.vatNumber && (
                      <div className="flex justify-between">
                        <dt className="text-gray-600">Numéro de TVA</dt>
                        <dd className="font-medium text-gray-900 text-right">{business.vatNumber}</dd>
                      </div>
                    )}
                    {business.intraCommunityVatNumber && (
                      <div className="flex justify-between">
                        <dt className="text-gray-600">TVA intracommunautaire</dt>
                        <dd className="font-medium text-gray-900 text-right">{business.intraCommunityVatNumber}</dd>
                      </div>
                    )}
                    {typeof business.shareCapital === 'number' && business.shareCapital > 0 && (
                      <div className="flex justify-between">
                        <dt className="text-gray-600">Capital social</dt>
                        <dd className="font-medium text-gray-900 text-right">
                          {new Intl.NumberFormat('fr-FR', {
                            style: 'currency',
                            currency: 'EUR',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0
                          }).format(business.shareCapital)}
                        </dd>
                      </div>
                    )}
                  </dl>
                </div>
              )}

              {getMapUrl(business) && (
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h2 className="text-lg font-bold text-gray-900 mb-4 uppercase tracking-wide border-l-4 border-green-500 pl-3">
                    Localisation
                  </h2>
                  <div className="rounded-lg overflow-hidden border border-gray-200 bg-gray-100" style={{ aspectRatio: '16/10', minHeight: 200 }}>
                    <iframe
                      title="Business location"
                      src={getMapUrl(business)}
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      className="block w-full h-full min-h-[200px]"
                    />
                  </div>
                  {getGoogleMapsSearchUrl(business) && (
                    <a
                      href={getGoogleMapsSearchUrl(business)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 inline-flex items-center text-sm text-teal-600 hover:text-teal-700 font-medium"
                    >
                      <ExternalLink className="h-4 w-4 mr-1" />
                      Ouvrir dans Google Maps
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <AlertTriangle className="h-6 w-6 text-red-600 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900">Delete Business</h3>
              </div>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete <strong>{business?.name}</strong>? This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleting}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center"
                >
                  {deleting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessDetail;
